import React from "react";
import {
  JockeyChange,
  RaceTypeCodeEnum,
  RaceWagerType
} from "@tvg/ts-types/Race";
import { BetButtons, Header, Icon, Tag } from "@tvg/design-system";
import type { UnaryFn } from "@tvg/ts-types/Functional";
import { SaddleClothSilk } from "@tvg/design-system/web/shared";
import { isFDR, isURP } from "@tvg/utils/generalUtils";
import RunnerChangeIndicator from "../RunnerChangeIndicator";
import { RunnerBI } from "../../../../hooks/useGetRunnerFromBI";
import {
  BetButtonsContainer,
  Column,
  Entry,
  JockeyTrainerContainer,
  HiddenPlaceholderTitle,
  Row,
  ScrLabelContainer,
  TextContainer,
  TitleButton
} from "./styled-components";

import Odds from "./Odds";
import OddsButton from "./OddsButton";
import RunnerTags from "../RunnerTags";

export interface RunnerHeaderInfoProps {
  onRunnerTitleClick: () => void;
  onClickBetButton: (
    type: string,
    isActive: boolean,
    position?: number
  ) => void;
  wagerType?: RaceWagerType;
  runnerBI: RunnerBI;
  isDriver: boolean;
  isInfoOpen: boolean;
  isDesktop: boolean;
  isLogged: boolean;
  isAmericanRace: boolean;
  activeButtons: boolean[];
  isWagerable: boolean;
  jockeyChanges: JockeyChange[];
  raceTypeCode: RaceTypeCodeEnum;
  allRunnersHasSilksError?: boolean;
  handleError?: UnaryFn<number, void>;
  trackDataSource?: string;
  showSilks?: boolean;
  hasRunnerSilk?: boolean;
}

const RunnerHeaderInfo = ({
  onClickBetButton,
  activeButtons,
  raceTypeCode,
  jockeyChanges,
  onRunnerTitleClick,
  wagerType,
  runnerBI,
  isWagerable = true,
  isDesktop = true,
  isInfoOpen = false,
  isDriver = false,
  isAmericanRace = false,
  isLogged = false,
  allRunnersHasSilksError,
  handleError,
  trackDataSource,
  showSilks,
  hasRunnerSilk = false
}: RunnerHeaderInfoProps) => {
  const columnCount = wagerType?.columnCount || 1;
  const useUpdatedVisual = isFDR() && isURP && !isDesktop;

  return (
    <Row hasPadding>
      <Column grow>
        <Row data-qa-label="race-card-info-container" hasPadding={false}>
          <SaddleClothSilk
            number={runnerBI.number}
            numberColor={runnerBI.numberColor}
            saddleColor={runnerBI.saddleColor}
            isScratched={runnerBI.isScratched}
            silkImage={runnerBI.silkImage}
            isAmericanRace={isAmericanRace}
            isDesktop={isDesktop}
            allRunnersHasSilksError={allRunnersHasSilksError}
            handleError={handleError}
            showSilks={showSilks}
            useUpdatedSilk={useUpdatedVisual}
          />
          <TextContainer
            data-qa-label="race-card-text-container"
            onClick={onRunnerTitleClick}
          >
            <TitleButton data-qa-label="race-card-dropdown-button">
              <HiddenPlaceholderTitle>
                <Header
                  tag="h3"
                  fontFamily="medium"
                  qaLabel="race-card-title-hidden"
                  lineHeight={20}
                >
                  {runnerBI.runnerName}
                </Header>
              </HiddenPlaceholderTitle>
              <Header
                tag="h3"
                fontFamily="medium"
                qaLabel="race-card-title"
                color="inherit"
                lineHeight={20}
              >
                {runnerBI.runnerName}
              </Header>
              <Icon
                qaLabel="race-card-dropdown-icon"
                size="s"
                name={isInfoOpen ? "chevronUp" : "chevronDown"}
                ml="space-1"
                lineColor="inherit"
              />
            </TitleButton>
            <JockeyTrainerContainer isScratched={runnerBI.isScratched}>
              <Entry data-qa-label="race-card-runner-jockey" hideEllipsis>
                {`${isDriver ? "D" : "J"}: ${runnerBI.jockey}`}
                {isLogged && (
                  <RunnerChangeIndicator
                    jockeyChanges={jockeyChanges}
                    raceTypeCode={raceTypeCode}
                  />
                )}
              </Entry>
              {!isDesktop && <span>|</span>}
              <Entry data-qa-label="race-card-runner-trainer">
                {`T: ${runnerBI.trainer}`}
              </Entry>
            </JockeyTrainerContainer>
            {!isDesktop && (
              <RunnerTags
                data={runnerBI.tags}
                isScratched={runnerBI.isScratched}
                handicapping={runnerBI.handicapping}
                timeform={runnerBI.timeform}
                trackDataSource={trackDataSource}
                isTotalOfTags={isInfoOpen}
                hasSilk={hasRunnerSilk}
                useUpdatedVisual={useUpdatedVisual}
              />
            )}
          </TextContainer>
          {!isDesktop && columnCount <= 1 && isWagerable ? (
            <OddsButton
              runnerBI={runnerBI}
              activeButtons={activeButtons}
              onClickBetButton={onClickBetButton}
            />
          ) : (
            <Odds runnerBI={runnerBI} isDesktop={isDesktop} />
          )}
        </Row>
        {!isInfoOpen && isDesktop && (
          <RunnerTags
            data={runnerBI.tags}
            isScratched={runnerBI.isScratched}
            handicapping={runnerBI.handicapping}
            timeform={runnerBI.timeform}
            trackDataSource={trackDataSource}
            isTotalOfTags={isInfoOpen}
            hasSilk={hasRunnerSilk}
            useUpdatedVisual={useUpdatedVisual}
          />
        )}
      </Column>
      {isDesktop && (
        <Column>
          {runnerBI.isScratched && (
            <ScrLabelContainer>
              <Tag
                size="m"
                variant="info"
                label="Scratched"
                qaLabel="race-card-scratched-label"
              />
            </ScrLabelContainer>
          )}
          {isWagerable && !runnerBI.isScratched && (
            <BetButtonsContainer useUpdatedVisual={useUpdatedVisual}>
              <BetButtons
                wagerType={wagerType}
                onPress={onClickBetButton}
                activeButtons={activeButtons}
              />
            </BetButtonsContainer>
          )}
        </Column>
      )}
    </Row>
  );
};

export default RunnerHeaderInfo;
